import { Alert, Button, Dropdown, Empty, Skeleton, Space, Typography } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import {
  PLATFORM_DESCRIPTIONS,
  PLATFORM_LABELS,
  useChannels,
  type ChannelPlatform,
} from '@entities/channel';
import { extractErrorMessage } from '@shared/api';
import { ChannelCard } from './ChannelCard';

const { Title, Text } = Typography;

interface Props {
  assistantId: string;
}

/**
 * Таб «Каналы» в карточке ассистента.
 *
 * После W3: модалка ConnectChannelModal заменена на отдельную страницу
 * /assistants/:id/channels/new?type=... Здесь — только список с кнопкой
 * «Подключить» (дропдаун выбора платформы).
 *
 * Клик по карточке канала → /assistants/:id/channels/:channelId.
 */
export function ChannelsTab({ assistantId }: Props) {
  const navigate = useNavigate();
  const { data: channels, isLoading, error, refetch } = useChannels(assistantId);

  const goToNew = (type: ChannelPlatform) => {
    navigate(`/assistants/${assistantId}/channels/new?type=${type}`);
  };

  if (isLoading) {
    return (
      <Space direction="vertical" style={{ width: '100%' }} size={12}>
        <Skeleton active paragraph={{ rows: 2 }} />
        <Skeleton active paragraph={{ rows: 2 }} />
      </Space>
    );
  }

  if (error) {
    return (
      <Alert
        type="error"
        showIcon
        message="Не удалось загрузить каналы"
        description={extractErrorMessage(error)}
        action={
          <Button size="small" onClick={() => refetch()}>
            Повторить
          </Button>
        }
      />
    );
  }

  const hasChannels = channels && channels.length > 0;

  // Дропдаун с тремя платформами для кнопки «Подключить».
  const platforms: ChannelPlatform[] = ['widget', 'telegram', 'max'];
  const menuItems = platforms.map((p) => ({
    key: p,
    label: (
      <Space direction="vertical" size={0} style={{ padding: '4px 0' }}>
        <Text strong>{PLATFORM_LABELS[p]}</Text>
        <Text type="secondary" style={{ fontSize: 11 }}>
          {PLATFORM_DESCRIPTIONS[p]}
        </Text>
      </Space>
    ),
    onClick: () => goToNew(p),
  }));

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 16,
        }}
      >
        <div>
          <Title level={5} style={{ margin: 0 }}>
            Подключённые каналы
          </Title>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Telegram, MAX и виджет на сайт — там где ваш ассистент отвечает пользователям
          </Text>
        </div>

        {hasChannels && (
          <Dropdown menu={{ items: menuItems }} placement="bottomRight">
            <Button type="primary" icon={<PlusOutlined />}>
              Подключить
            </Button>
          </Dropdown>
        )}
      </div>

      {!hasChannels ? (
        <Empty
          description="Каналов пока нет. Подключите первый — ассистент начнёт отвечать."
          style={{ padding: '24px 0' }}
        >
          <Dropdown menu={{ items: menuItems }} placement="bottomRight">
            <Button type="primary" icon={<PlusOutlined />}>
              Подключить канал
            </Button>
          </Dropdown>
        </Empty>
      ) : (
        <Space direction="vertical" style={{ width: '100%' }} size={12}>
          {channels.map((channel) => (
            <ChannelCard
              key={channel.id}
              channel={channel}
              assistantId={assistantId}
            />
          ))}
        </Space>
      )}
    </div>
  );
}
